import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { SegmentControl, Tooltip } from '@blade/ui-kit'
import { useNetworkLinks } from '@root/entities/network'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { StatisticsPriceTypeTabs } from '@root/features/profile/board-statistics/ui/common/statistics-price-type-tabs'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import { reatomUserTradingAnalytics } from '@root/features/user/user-trading-analytics/atoms'
import {
  USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP,
  USER_TRADING_ANALYTICS_PERIODS,
} from '@root/features/user/user-trading-analytics/models'
import {
  AnalysisBlock,
  AvgBuyMcDistributionBlock,
  DistributionBlock,
  UserProfilePnl,
} from '@root/features/user/user-trading-analytics/ui'
import { PlatformUtil } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { LinkIcon } from '@root/shared/ui-kit'

import { reatomWalletProfileModel } from '../atoms'

import { WalletProfileOwner } from './wallet-profile-owner'
import { WalletProfileTabs } from './wallet-profile-tabs'

import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models'

/**
 * A wallet's trading record, drawn by the dashboard the terminal already has.
 *
 * Almost nothing here is new drawing. The PnL chart, the calendar behind it,
 * the distribution bars, the entry market caps and the analysis grid are the
 * components the profile screens use, handed a different source — see
 * {@link reatomWalletProfileModel}.
 *
 * The phishing block is deliberately absent. Nothing here scores a wallet for
 * it, and an empty block says "we checked, it is clean" rather than "we did
 * not check".
 */

type Props = {
  walletAddress: Address
  network: Network.Network
}

export const WalletProfileContent: React.FC<Props> = ({
  walletAddress,
  network,
}) => {
  const { t } = useTranslation()
  const { buildContractLink } = useNetworkLinks()

  // Built per wallet, so opening a different one fetches rather than showing
  // the last one's figures.
  const wallet = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomWalletProfileModel({ walletAddress, network }),
    [walletAddress, network],
  )
  const model = useMemo(
    () =>
      reatomUserTradingAnalytics(
        { delugeId: '' as never, wallets: [], source: wallet.analyticsAtom },
        'model',
      ),
    [wallet],
  )
  const [profile] = useAtom(wallet.profileAtom)

  const [period, setPeriod] = useAtom(model.periodAtom)
  const [priceType, setPriceType] = useAtom(model.priceTypeAtom)

  return (
    <Root>
      <Head>
        <WalletAddressTruncated
          walletAddress={walletAddress}
          withCopy
          copySize={12}
        />
        <Tooltip content={t('transaction.see_in_explorer')}>
          <ExplorerButton
            type="button"
            onClick={() =>
              PlatformUtil.openLink(buildContractLink(walletAddress), '_blank')
            }
          >
            <LinkIcon size={12} />
          </ExplorerButton>
        </Tooltip>

        {/* Whose wallet this is, when it is anybody's here. */}
        {profile.value?.owner ? (
          <WalletProfileOwner owner={profile.value.owner} />
        ) : undefined}

        <Spacer />

        <SegmentControlStyled value={period} onChange={setPeriod}>
          {USER_TRADING_ANALYTICS_PERIODS.map((each) => (
            <SegmentControl.Item key={each} value={each}>
              {t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[each]).toUpperCase()}
            </SegmentControl.Item>
          ))}
        </SegmentControlStyled>

        <StatisticsPriceTypeTabs
          priceType={priceType}
          onPriceTypeChange={setPriceType}
        />
      </Head>

      <Body>
        <Dashboard>
          <Pnl model={model} owner={ProfileOwner.AnotherUsers} />
          <Analysis model={model} />
          <Distribution>
            <DistributionBlock model={model} />
          </Distribution>
          <AvgBuyMc model={model} />
        </Dashboard>

        <WalletProfileTabs
          walletAddress={walletAddress}
          network={network}
          positions={profile.value?.positions ?? []}
        />
      </Body>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  width: min(1020px, 92vw);
  height: min(640px, 82vh);
  padding: 0 16px 16px;

  /* A phone gets the whole screen: 92vw of a 390px window is a column of
     numbers nobody can read, and the dialog has nothing to share it with. */
  @media ${({ theme }) => theme.mobile.queryMaxWidth} {
    width: 100vw;
    height: 100dvh;
    padding: 0 12px 12px;
  }
`

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  padding-block: 4px 12px;
`

const Spacer = styled.div`
  flex: 1;
`

const SegmentControlStyled = styled(SegmentControl<UserTradingAnalyticsPeriod>)`
  height: 26px;
`

const ExplorerButton = styled.button`
  display: grid;
  place-items: center;

  padding: 3px;
  border: none;
  border-radius: 4px;

  color: var(--text-9);
  background: transparent;
  cursor: pointer;

  &:hover {
    color: var(--text-11);
    background: var(--appearanceA-2);
  }
`

const Body = styled.div`
  overflow: auto;
  display: grid;
  align-content: start;
  gap: 12px;

  /* The scrollbar sits outside the cards rather than cutting through them. */
  padding-right: 2px;
`

const Dashboard = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas:
    'chart chart analysis'
    'distribution distribution avg-buy-mc';

  /* Fixed row heights cropped the chart on a short window; the blocks set
     their own and the rows follow. */
  grid-auto-rows: minmax(260px, auto);

  @media ${({ theme }) => theme.tablet.queryMaxWidth} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'chart chart'
      'analysis avg-buy-mc'
      'distribution distribution';
  }

  @media ${({ theme }) => theme.mobile.queryMaxWidth} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'chart'
      'analysis'
      'distribution'
      'avg-buy-mc';
  }
`

const Pnl = styled(UserProfilePnl)`
  grid-area: chart;
`

const Analysis = styled(AnalysisBlock)`
  grid-area: analysis;
`

const Distribution = styled.div`
  grid-area: distribution;
  display: grid;
`

const AvgBuyMc = styled(AvgBuyMcDistributionBlock)`
  grid-area: avg-buy-mc;
`
